import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '@/contexts/AuthContext';

interface UseSocketOptions {
  autoConnect?: boolean;
  reconnection?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
}

interface UseSocketReturn {
  socket: Socket | null;
  connected: boolean;
  error: string | null;
  connect: () => void;
  disconnect: () => void;
}

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3300';

export function useSocket(options: UseSocketOptions = {}): Socket | null {
  const {
    autoConnect = true,
    reconnection = true,
    reconnectionAttempts = 10,
    reconnectionDelay = 2000
  } = options;

  // The scan-progress websocket gateway derives its tenant (workgroupid) from the JWT
  // presented at connect time, and scopes event access to that workgroup for the life of
  // the connection. If the user switches their active workgroup, the JWT in localStorage
  // changes (AuthContext.switchWorkgroup swaps it in *before* bumping workgroupEpoch), but a
  // socket that connected under the old token would otherwise keep running under the old
  // workgroup's auth context indefinitely. Including workgroupEpoch in this effect's
  // dependency array forces a full teardown (cleanup below closes the old socket) and
  // reconnect with the fresh token every time the active workgroup changes.
  const { workgroupEpoch } = useAuth();

  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reconnectAttempt = useRef(0);

  useEffect(() => {
    if (!autoConnect) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setError('No authentication token found');
      setSocket(null);
      return;
    }

    const socketInstance = io(SOCKET_URL, {
      auth: {
        token: token
      },
      reconnection,
      reconnectionAttempts,
      reconnectionDelay,
      transports: ['websocket', 'polling'],
      timeout: 10000,
      forceNew: false,
      autoConnect: true
    });

    setSocket(socketInstance);

    const handleConnect = () => {
      setConnected(true);
      setError(null);
      reconnectAttempt.current = 0;
    };

    const handleDisconnect = (reason: string) => {
      setConnected(false);

      if (reason === 'io server disconnect') {
        setTimeout(() => {
          if (reconnectAttempt.current < reconnectionAttempts) {
            reconnectAttempt.current++;
            socketInstance.connect();
          }
        }, reconnectionDelay);
      }
    };

    const handleConnectError = (err: Error) => {
      setError(err.message);
      setConnected(false);
    };

    const handleReconnect = (attemptNumber: number) => {
      setConnected(true);
      setError(null);
    };

    const handleReconnectError = (err: Error) => {
      setError(`Reconnection failed: ${err.message}`);
    };

    const handleReconnectFailed = () => {
      setError('Failed to reconnect to server');
      setConnected(false);
    };

    socketInstance.on('connect', handleConnect);
    socketInstance.on('disconnect', handleDisconnect);
    socketInstance.on('connect_error', handleConnectError);
    socketInstance.on('reconnect', handleReconnect);
    socketInstance.on('reconnect_error', handleReconnectError);
    socketInstance.on('reconnect_failed', handleReconnectFailed);

    setSocket(socketInstance);

    return () => {
      socketInstance.off('connect', handleConnect);
      socketInstance.off('disconnect', handleDisconnect);
      socketInstance.off('connect_error', handleConnectError);
      socketInstance.off('reconnect', handleReconnect);
      socketInstance.off('reconnect_error', handleReconnectError);
      socketInstance.off('reconnect_failed', handleReconnectFailed);
      // Tear the old connection down completely (rather than leaving it to linger) before
      // this effect re-runs to open a new one -- this is what actually forces the reconnect
      // when workgroupEpoch changes.
      socketInstance.close();
      setSocket(null);
      setConnected(false);
    };
  }, [autoConnect, reconnection, reconnectionAttempts, reconnectionDelay, workgroupEpoch]);

  return socket;
}

export function useSocketState(options: UseSocketOptions = {}): UseSocketReturn {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = () => {
    if (socket && !connected) {
      socket.connect();
    }
  };

  const disconnect = () => {
    if (socket && connected) {
      socket.disconnect();
    }
  };

  useEffect(() => {
    const socketInstance = useSocket(options);
    setSocket(socketInstance);

    if (socketInstance) {
      const handleConnect = () => setConnected(true);
      const handleDisconnect = () => setConnected(false);
      const handleError = (err: Error) => setError(err.message);

      socketInstance.on('connect', handleConnect);
      socketInstance.on('disconnect', handleDisconnect);
      socketInstance.on('connect_error', handleError);

      setConnected(socketInstance.connected);

      return () => {
        socketInstance.off('connect', handleConnect);
        socketInstance.off('disconnect', handleDisconnect);
        socketInstance.off('connect_error', handleError);
      };
    }
  }, []);

  return {
    socket,
    connected,
    error,
    connect,
    disconnect
  };
}
